export const ACCOUNT_STATUS_STRIKE_PAUSED = 'Strike Paused';
export const ACCOUNT_STATUS_REINSTATED = 'Reinstated';

export const LATE_CANCEL_STRIKE_POLICY_VERSION = 'late-cancel-strikes-v1';
export const LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS = Date.UTC(2026, 6, 31, 7, 0, 0);

const LA_TIME_ZONE = 'America/Los_Angeles';
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const STRIKE_LIMIT = 3;

const getDeviceTimeZone = () => (
  Intl.DateTimeFormat().resolvedOptions().timeZone || LA_TIME_ZONE
);

const getZonedDateParts = (dateMs, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(new Date(dateMs));
  const getPart = (type) => Number(parts.find((part) => part.type === type)?.value || 0);

  return {
    year: getPart('year'),
    month: getPart('month'),
    day: getPart('day'),
    hour: getPart('hour'),
    minute: getPart('minute'),
    second: getPart('second'),
  };
};

const getTimeZoneOffsetMs = (dateMs, timeZone) => {
  const parts = getZonedDateParts(dateMs, timeZone);
  const asUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return asUtcMs - dateMs;
};

const zonedPartsToUtcMs = (parts, timeZone) => {
  const wallClockUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second || 0
  );
  const firstPassOffset = getTimeZoneOffsetMs(wallClockUtcMs, timeZone);
  const firstPassUtcMs = wallClockUtcMs - firstPassOffset;
  const secondPassOffset = getTimeZoneOffsetMs(firstPassUtcMs, timeZone);

  return wallClockUtcMs - secondPassOffset;
};

export const getClassDateInInstructorLocalWallClock = ({
  classDateMs,
  localTimeZone = getDeviceTimeZone(),
} = {}) => {
  const classDateNumber = Number(classDateMs);
  if (!Number.isFinite(classDateNumber)) return NaN;

  return zonedPartsToUtcMs(
    getZonedDateParts(classDateNumber, LA_TIME_ZONE),
    localTimeZone
  );
};

const normalizeStatus = (status) => String(status || '').trim() || 'Active';

const getRepeatIdentifier = (classData = {}) => (
  classData.RepeatIdentifier || classData.RepeatClassIdentifier || null
);

const getClassId = (classData = {}) => (
  classData.id || classData.ClassID || classData.classId || ''
);

const getClassDateMs = (classData = {}) => {
  const dateMs = Number(classData.Date ?? classData.ClassDate);
  return Number.isFinite(dateMs) ? dateMs : null;
};

export const getSixMonthStrikeWindowStartMs = (nowMs = Date.now()) => {
  const now = new Date(Number(nowMs));
  if (Number.isNaN(now.getTime())) return LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS;

  const sixMonthsAgo = new Date(now.getTime());
  sixMonthsAgo.setUTCMonth(sixMonthsAgo.getUTCMonth() - 6);

  return Math.max(sixMonthsAgo.getTime(), LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS);
};

export const isStrikeEligibleCancellationEntry = (entry = {}, {
  nowMs = Date.now(),
  windowStartMs = getSixMonthStrikeWindowStartMs(nowMs),
} = {}) => {
  const cancellationTime = Number(entry.CancellationTime);
  return entry.StrikeEligible === true
    && Number.isFinite(cancellationTime)
    && cancellationTime >= windowStartMs
    && cancellationTime >= LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS;
};

export const getLateCancellationStrikeCount = ({
  lateCanceledClassArray = [],
  nowMs = Date.now(),
  accountStatus = 'Active',
  strikeState = {},
} = {}) => {
  const status = normalizeStatus(accountStatus);
  if (status === ACCOUNT_STATUS_STRIKE_PAUSED) return STRIKE_LIMIT;
  if (status === ACCOUNT_STATUS_REINSTATED) return STRIKE_LIMIT - 1;

  const reinstatedAt = Number(strikeState?.reinstatedAt || 0);
  const windowStartMs = Math.max(
    getSixMonthStrikeWindowStartMs(nowMs),
    Number.isFinite(reinstatedAt) ? reinstatedAt : 0
  );

  return lateCanceledClassArray.filter((entry) => (
    isStrikeEligibleCancellationEntry(entry, { nowMs, windowStartMs })
  )).length;
};

export const isLateCancelStrikeEligibleClass = ({
  classData = {},
  nowMs = Date.now(),
  cancellationTime = nowMs,
  localTimeZone = getDeviceTimeZone(),
} = {}) => {
  const timing = getLateCancelStrikeTiming({
    classData,
    nowMs,
    cancellationTime,
    localTimeZone,
  });

  return timing.StrikeEligible;
};

export const getLateCancelStrikeTiming = ({
  classData = {},
  nowMs = Date.now(),
  cancellationTime = nowMs,
  localTimeZone = getDeviceTimeZone(),
} = {}) => {
  const policyTime = Number(cancellationTime);
  if (!Number.isFinite(policyTime) || policyTime < LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS) {
    return {
      StrikeEligible: false,
      WithinTwoHours: false,
      WithinTwentyFourHours: false,
      TimeUntilClassMs: null,
    };
  }

  const classDateMs = getClassDateMs(classData);
  if (!Number.isFinite(classDateMs)) {
    return {
      StrikeEligible: false,
      WithinTwoHours: false,
      WithinTwentyFourHours: false,
      TimeUntilClassMs: null,
    };
  }

  const localWallClockClassDateMs = getClassDateInInstructorLocalWallClock({
    classDateMs,
    localTimeZone,
  });
  const timeUntilClassMs = localWallClockClassDateMs - Number(nowMs);
  const isFutureOrNow = Number.isFinite(timeUntilClassMs) && timeUntilClassMs >= 0;
  const withinTwoHours = isFutureOrNow && timeUntilClassMs <= TWO_HOURS_MS;
  const withinTwentyFourHours = isFutureOrNow && timeUntilClassMs <= TWENTY_FOUR_HOURS_MS;

  return {
    StrikeEligible: withinTwentyFourHours,
    WithinTwoHours: withinTwoHours,
    WithinTwentyFourHours: withinTwentyFourHours,
    TimeUntilClassMs: Number.isFinite(timeUntilClassMs) ? timeUntilClassMs : null,
  };
};

export const buildLateCancellationStrikeEntries = ({
  selectedDropClasses = [],
  reason = '',
  reasonOther = '',
  cancellationTime = Date.now(),
  nowMs = cancellationTime,
  existingStrikeCount = 0,
  localTimeZone = getDeviceTimeZone(),
} = {}) => {
  let nextStrikeNumber = Number(existingStrikeCount || 0);

  return selectedDropClasses.map((classData) => {
    const timing = getLateCancelStrikeTiming({
      classData,
      nowMs,
      cancellationTime,
      localTimeZone,
    });
    const strikeEligible = timing.StrikeEligible;
    const strikeNumber = strikeEligible ? Math.min(nextStrikeNumber + 1, STRIKE_LIMIT) : null;
    if (strikeEligible) nextStrikeNumber += 1;

    return {
      CancellationReason: reason || '',
      CancellationReasonOther: reason === 'Other' ? reasonOther || '' : '',
      CancellationTime: cancellationTime,
      ClassID: getClassId(classData),
      ClassDate: getClassDateMs(classData),
      PolicyVersion: LATE_CANCEL_STRIKE_POLICY_VERSION,
      RepeatIdentifier: getRepeatIdentifier(classData),
      StrikeEligible: strikeEligible,
      StrikeNumber: strikeNumber,
      WithinTwoHours: timing.WithinTwoHours,
      WithinTwentyFourHours: timing.WithinTwentyFourHours,
    };
  });
};

export const getLateCancellationStrikeImpact = ({
  accountStatus = 'Active',
  lateCanceledClassArray = [],
  selectedDropClasses = [],
  nowMs = Date.now(),
  cancellationTime = nowMs,
  localTimeZone = getDeviceTimeZone(),
  strikeState = {},
} = {}) => {
  const currentStrikeCount = getLateCancellationStrikeCount({
    accountStatus,
    lateCanceledClassArray,
    nowMs,
    strikeState,
  });
  const strikeEligibleClasses = selectedDropClasses.filter((classData) => (
    isLateCancelStrikeEligibleClass({
      classData,
      nowMs,
      cancellationTime,
      localTimeZone,
    })
  ));
  const newStrikeCount = strikeEligibleClasses.length;
  const projectedStrikeCount = Math.min(STRIKE_LIMIT, currentStrikeCount + newStrikeCount);
  const shouldStrikePause = newStrikeCount > 0 && projectedStrikeCount >= STRIKE_LIMIT;

  return {
    accountStatus: normalizeStatus(accountStatus),
    currentStrikeCount,
    newStrikeCount,
    projectedStrikeCount,
    remainingBeforePause: Math.max(0, STRIKE_LIMIT - projectedStrikeCount),
    shouldStrikePause,
    nextAccountStatus: shouldStrikePause ? ACCOUNT_STATUS_STRIKE_PAUSED : normalizeStatus(accountStatus),
    strikeEligibleClassIds: strikeEligibleClasses.map(getClassId).filter(Boolean),
  };
};

export const buildLateCancellationStrikeState = ({
  accountStatus = 'Active',
  lateCanceledClassArray = [],
  nowMs = Date.now(),
  strikeState = {},
} = {}) => {
  const status = normalizeStatus(accountStatus);
  const currentCount = getLateCancellationStrikeCount({
    accountStatus: status,
    lateCanceledClassArray,
    nowMs,
    strikeState,
  });
  const latestStrikeAt = lateCanceledClassArray
    .filter((entry) => isStrikeEligibleCancellationEntry(entry, { nowMs }))
    .reduce((latest, entry) => Math.max(latest, Number(entry.CancellationTime || 0)), 0);

  return {
    policyVersion: LATE_CANCEL_STRIKE_POLICY_VERSION,
    policyEffectiveAtMs: LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS,
    accountStatus: status,
    status: status === ACCOUNT_STATUS_STRIKE_PAUSED
      ? 'strike_paused'
      : status === ACCOUNT_STATUS_REINSTATED
        ? 'reinstated'
        : 'active',
    currentCount,
    remainingBeforePause: Math.max(0, STRIKE_LIMIT - currentCount),
    latestStrikeAt: latestStrikeAt || null,
    updatedAt: nowMs,
  };
};

export const buildLateCancellationStrikeStateAfterDrop = ({
  impact = {},
  droppedFutureClassIds = [],
  nowMs = Date.now(),
  actor = null,
} = {}) => {
  const isPaused = impact.shouldStrikePause === true;
  const currentCount = Number(impact.projectedStrikeCount || 0);

  return {
    policyVersion: LATE_CANCEL_STRIKE_POLICY_VERSION,
    policyEffectiveAtMs: LATE_CANCEL_STRIKE_POLICY_EFFECTIVE_AT_MS,
    accountStatus: isPaused ? ACCOUNT_STATUS_STRIKE_PAUSED : impact.nextAccountStatus || 'Active',
    status: isPaused ? 'strike_paused' : 'active',
    currentCount,
    remainingBeforePause: Math.max(0, STRIKE_LIMIT - currentCount),
    latestStrikeAt: nowMs,
    updatedAt: nowMs,
    ...(isPaused ? { blockedAt: nowMs, blockedBy: actor } : {}),
    strikeEligibleClassIds: impact.strikeEligibleClassIds || [],
    droppedFutureClassIds,
  };
};
